import React, { useEffect, useMemo, useState } from 'react';
import {
  Drawer,
  Form,
  Input,
  Select,
  Button,
  Space,
  Switch,
  Divider,
  Alert,
  message,
  Tag,
} from 'antd';
import { UserAddOutlined, SaveOutlined } from '@ant-design/icons';
import { Business } from '../../../types/business';
import {
  CreateSuperAdminUserInput,
  SuperAdminUser,
  useCreateSuperAdminUser,
  useUpdateSuperAdminUser,
} from '../../../hooks/useSuperAdmin';
import RoleProfileSelectForBusiness from './RoleProfileSelectForBusiness';
import { useAuth } from '../../../contexts/AuthContext';

const { Option } = Select;

interface UserFormDrawerProps {
  open: boolean;
  onClose: () => void;
  businesses: Business[];
  initialUser?: (SuperAdminUser & { businessNames?: string[] }) | null;
  onSuccess?: () => void;
}

const UserFormDrawer: React.FC<UserFormDrawerProps> = ({
  open,
  onClose,
  businesses,
  initialUser,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const isEditMode = Boolean(initialUser);
  const { isSuperAdmin: currentUserIsSuperAdmin, isCSM: currentUserIsCSM } = useAuth();

  const createUserMutation = useCreateSuperAdminUser();
  const updateUserMutation = useUpdateSuperAdminUser();
  const loading = createUserMutation.isPending || updateUserMutation.isPending;
  
  const defaultBusinessId = useMemo(() => businesses[0]?.id, [businesses]);
  const showSuperAdminSwitch = currentUserIsSuperAdmin; // CSM ne voit pas le switch Super Admin

  useEffect(() => {
    if (open) {
      if (isEditMode && initialUser) {
        const userBusinessId = initialUser.currentBusinessId || initialUser.businessIds[0];
        
        // Construire roleProfileIds depuis initialUser
        const roleProfileIds: { [businessId: string]: string } = initialUser.roleProfileIds || {};
        
        console.log('🔍 Initialisation formulaire - initialUser complet:', {
          id: initialUser.id,
          name: initialUser.name,
          roleProfileIds: initialUser.roleProfileIds,
          businessIds: initialUser.businessIds,
        });
        console.log('🔍 Initialisation formulaire - roleProfileIds construit:', roleProfileIds);
        
        form.setFieldsValue({
          name: initialUser.name,
          email: initialUser.email,
          role: initialUser.role || undefined, // ✅ NOUVEAU : Rôle métier
          roleProfileIds, // Profils par business
          isSuperAdmin: initialUser.isSuperAdmin,
          isCSM: initialUser.isCSM,
          businessIds: initialUser.businessIds,
          currentBusinessId: userBusinessId,
          isActive: initialUser.isActive,
          phone: initialUser.phone || undefined,
          password: undefined,
        });
        
        // Vérifier que les valeurs sont bien dans le formulaire
        const formValues = form.getFieldsValue();
        console.log('🔍 Valeurs dans le formulaire après setFieldsValue:', {
          roleProfileIds: formValues.roleProfileIds,
          businessIds: formValues.businessIds,
        });
      } else {
        form.resetFields();
        form.setFieldsValue({
          roleProfileIds: {},
          isSuperAdmin: false,
          isCSM: false,
          businessIds: defaultBusinessId ? [defaultBusinessId] : [],
          currentBusinessId: defaultBusinessId,
          isActive: true,
        });
      }
    }
  }, [open, isEditMode, initialUser, form, defaultBusinessId]);

  const handleBusinessChange = (ids: string[]) => {
    const current = form.getFieldValue('currentBusinessId');
    if (!ids.includes(current)) {
      const newCurrent = ids[0] || undefined;
      form.setFieldsValue({
        currentBusinessId: newCurrent,
      });
    }
  };

  const handleSuperAdminChange = (checked: boolean) => {
    form.setFieldsValue({
      isSuperAdmin: checked,
      isCSM: checked ? false : form.getFieldValue('isCSM'),
      ...(checked ? { businessIds: [], currentBusinessId: undefined, roleProfileIds: {} } : {}),
    });
  };

  const handleCSMChange = (checked: boolean) => {
    form.setFieldsValue({
      isCSM: checked,
      isSuperAdmin: checked ? false : form.getFieldValue('isSuperAdmin'),
    });
  };

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const formIsSuperAdmin = form.getFieldValue('isSuperAdmin') ?? values.isSuperAdmin ?? false;
      // Lecture explicite isCSM (Switch) pour création et mise à jour
      const formIsCSM = Boolean(form.getFieldValue('isCSM') ?? values.isCSM ?? false);
      const roleProfileIds = form.getFieldValue('roleProfileIds') || values.roleProfileIds || {};
      const businessIds = formIsSuperAdmin ? [] : (form.getFieldValue('businessIds') || values.businessIds || []);
      const currentBusinessId = formIsSuperAdmin ? undefined : (values.currentBusinessId ?? form.getFieldValue('currentBusinessId'));

      // Validation : si pas Super Admin, chaque business doit avoir un profil de rôle
      if (!formIsSuperAdmin) {
        const missingProfiles = businessIds.filter((businessId: string) => {
          const profileId = roleProfileIds[businessId];
          return !profileId || String(profileId).trim() === '';
        });
        if (missingProfiles.length > 0) {
          const missingBusinessNames = missingProfiles
            .map((id: string) => businesses.find(b => b.id === id)?.name || id)
            .join(', ');
          message.error(`Veuillez sélectionner un profil de rôle pour chaque business : ${missingBusinessNames}`);
          return;
        }
        if (businessIds.length === 0) {
          message.error('Sélectionnez au moins un business pour un utilisateur non Super Admin');
          return;
        }
      }

      if (isEditMode && initialUser) {
        await updateUserMutation.mutateAsync({
          userId: initialUser.id,
          name: values.name.trim(),
          email: values.email.trim().toLowerCase(),
          role: values.role?.trim() || undefined,
          roleProfileIds: formIsSuperAdmin ? undefined : roleProfileIds,
          isSuperAdmin: showSuperAdminSwitch ? (formIsSuperAdmin || false) : undefined,
          isCSM: currentUserIsSuperAdmin ? formIsCSM : undefined,
          businessIds: businessIds.length ? businessIds : undefined,
          currentBusinessId: currentBusinessId || undefined,
          isActive: values.isActive,
          phone: values.phone?.trim(),
          password: values.password || undefined,
        });
        message.success('Utilisateur mis à jour avec succès');
      } else {
        const createPayload: CreateSuperAdminUserInput = {
          name: values.name.trim(),
          email: values.email.trim().toLowerCase(),
          role: values.role?.trim() || undefined,
          roleProfileIds: formIsSuperAdmin ? {} : roleProfileIds,
          isSuperAdmin: showSuperAdminSwitch ? (formIsSuperAdmin || false) : false,
          isCSM: currentUserIsSuperAdmin ? formIsCSM : false,
          businessIds,
          currentBusinessId: formIsSuperAdmin ? '' : (currentBusinessId || defaultBusinessId || ''),
          password: values.password,
          phone: values.phone?.trim(),
          isActive: values.isActive,
        };
        await createUserMutation.mutateAsync(createPayload);
        message.success('Utilisateur créé avec succès');
      }

      handleClose();
      onSuccess?.();
    } catch (error) {
      console.error('Erreur formulaire utilisateur:', error);
    }
  };

  return (
    <Drawer
      title={
        <Space align="center">
          {isEditMode ? <SaveOutlined /> : <UserAddOutlined />}
          <span>{isEditMode ? 'Modifier un utilisateur' : 'Créer un utilisateur'}</span>
        </Space>
      }
      open={open}
      onClose={handleClose}
      width={520}
      destroyOnClose
      extra={
        <Space>
          <Button onClick={handleClose}>Annuler</Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={loading}
            icon={isEditMode ? <SaveOutlined /> : <UserAddOutlined />}
          >
            {isEditMode ? 'Enregistrer' : 'Créer'}
          </Button>
        </Space>
      }
    >
      <Form layout="vertical" form={form}>
        <Alert
          message="Les utilisateurs créés recevront un mot de passe temporaire. Pensez à leur communiquer."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form.Item
          name="name"
          label="Nom complet"
          rules={[
            { required: true, message: 'Nom requis' },
            { min: 3, message: 'Minimum 3 caractères' },
          ]}
        >
          <Input placeholder="Nom de l'utilisateur" />
        </Form.Item>

        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: 'Email requis' },
            { type: 'email', message: 'Format email invalide' },
          ]}
        >
          <Input placeholder="email@entreprise.com" />
        </Form.Item>

        <Form.Item
          name="phone"
          label="Téléphone (optionnel)"
          rules={[
            { pattern: /^(\+?\d{6,15})?$/, message: 'Numéro invalide' },
          ]}
        >
          <Input placeholder="+237699000000" />
        </Form.Item>

        <Form.Item
          shouldUpdate={(prev, curr) => prev.isSuperAdmin !== curr.isSuperAdmin}
          noStyle
        >
          {({ getFieldValue }) => {
            const isSuperAdmin = getFieldValue('isSuperAdmin');
            if (isSuperAdmin) return null; // SuperAdmin n'a pas de rôle métier
            
            return (
              <Form.Item
                name="role"
                label="Rôle métier (optionnel)"
                tooltip="Rôle de l'utilisateur dans le business (ex: Manager, Caissier, Esthéticienne)"
              >
                <Input 
                  placeholder="Ex: Manager, Caissier, Esthéticienne"
                  allowClear
                />
              </Form.Item>
            );
          }}
        </Form.Item>

        {/* Champ caché pour que le formulaire suive roleProfileIds */}
        <Form.Item name="roleProfileIds" hidden>
          <Input type="hidden" />
        </Form.Item>

        <Form.Item
          shouldUpdate={(prev, curr) => prev.businessIds !== curr.businessIds || prev.currentBusinessId !== curr.currentBusinessId || prev.roleProfileIds !== curr.roleProfileIds || prev.isSuperAdmin !== curr.isSuperAdmin}
          noStyle
        >
          {({ getFieldValue }) => {
            const selectedBusinessIds = getFieldValue('businessIds') || [];
            const roleProfileIds = getFieldValue('roleProfileIds') || {};
            const currentBusinessId = getFieldValue('currentBusinessId');
            const formIsSuperAdmin = getFieldValue('isSuperAdmin');
            
            if (formIsSuperAdmin) return null; // Super Admin n'a pas de profils par business
            
            return (
              <>
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ marginBottom: '12px', fontWeight: 'bold' }}>
                    Profils de permissions par business
                  </div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>
                    Sélectionnez un profil de rôle pour chaque business. Si un business n'a pas de profil défini, l'utilisateur n'aura pas accès à ce business.
                  </div>
                  
                  {selectedBusinessIds.length === 0 ? (
                    <Alert
                      message="Sélectionnez d'abord les businesses accessibles"
                      type="info"
                      showIcon
                      style={{ marginBottom: '16px' }}
                    />
                  ) : (
                    selectedBusinessIds.map((businessId: string) => {
                      const business = businesses.find(b => b.id === businessId);
                      // Charger les profils pour ce business (on utilisera selectedBusinessForProfiles pour simplifier)
                      return (
                        <Form.Item
                          key={businessId}
                          label={
                            <span>
                              {business?.name || businessId}
                              {businessId === currentBusinessId && (
                                <Tag color="blue" style={{ marginLeft: '8px' }}>Par défaut</Tag>
                              )}
                            </span>
                          }
                          style={{ marginBottom: '16px' }}
                        >
                          <RoleProfileSelectForBusiness
                            businessId={businessId}
                            value={roleProfileIds[businessId]}
                            onChange={(value) => {
                              const current = getFieldValue('roleProfileIds') || {};
                              form.setFieldsValue({
                                roleProfileIds: {
                                  ...current,
                                  [businessId]: value
                                }
                              });
                            }}
                            placeholder="Sélectionner un profil"
                          />
                        </Form.Item>
                      );
                    })
                  )}
                </div>
              </>
            );
          }}
        </Form.Item>
        
        {showSuperAdminSwitch && (
          <Form.Item
            name="isSuperAdmin"
            label="Super Admin"
            valuePropName="checked"
            tooltip="Cocher pour donner les permissions Super Admin (gestion de tous les businesses). Incompatible avec CSM et avec la sélection de businesses."
          >
            <Switch checkedChildren="Oui" unCheckedChildren="Non" onChange={handleSuperAdminChange} />
          </Form.Item>
        )}

        {currentUserIsSuperAdmin && (
          <Form.Item
            name="isCSM"
            label="Customer Success Manager (CSM)"
            valuePropName="checked"
            tooltip="Cocher pour désigner un CSM (lié aux businesses, sans droit Super Admin). Incompatible avec Super Admin."
          >
            <Switch checkedChildren="Oui" unCheckedChildren="Non" onChange={handleCSMChange} />
          </Form.Item>
        )}

        <Form.Item
          shouldUpdate={(prev, curr) => prev.isSuperAdmin !== curr.isSuperAdmin || prev.isCSM !== curr.isCSM}
          noStyle
        >
          {({ getFieldValue }) => {
            const formIsSuperAdmin = getFieldValue('isSuperAdmin');
            const businessesDisabled = formIsSuperAdmin;
            return (
              <Form.Item
                name="businessIds"
                label="Businesses accessibles"
                rules={[{ required: !businessesDisabled, message: 'Sélectionnez au moins un business' }]}
              >
                <Select
                  mode="multiple"
                  placeholder={businessesDisabled ? 'Super Admin : pas de business' : 'Sélectionner un ou plusieurs businesses'}
                  onChange={handleBusinessChange}
                  disabled={businessesDisabled}
                >
                  {businesses.map((business) => (
                    <Option key={business.id} value={business.id}>
                      {business.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            );
          }}
        </Form.Item>

        <Form.Item
          shouldUpdate={(prev, curr) => prev.businessIds !== curr.businessIds || prev.isSuperAdmin !== curr.isSuperAdmin}
          noStyle
        >
          {({ getFieldValue }) => {
            const selectedBusinessIds = getFieldValue('businessIds') || [];
            const formIsSuperAdmin = getFieldValue('isSuperAdmin');
            const disabled = formIsSuperAdmin || selectedBusinessIds.length === 0;
            return (
              <Form.Item
                name="currentBusinessId"
                label="Business par défaut"
                rules={[{ required: !formIsSuperAdmin, message: 'Sélectionnez un business par défaut' }]}
                tooltip="Le profil de permissions sera défini pour ce business. Pour les utilisateurs multi-business, le profil s'applique au business par défaut."
              >
                <Select
                  placeholder={formIsSuperAdmin ? 'Super Admin : non applicable' : 'Business principal'}
                  disabled={disabled}
                >
                  {businesses
                    .filter((business) => selectedBusinessIds.includes(business.id))
                    .map((business) => (
                      <Option key={business.id} value={business.id}>
                        {business.name}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            );
          }}
        </Form.Item>

        <Divider />

        <Form.Item
          name="password"
          label={isEditMode ? 'Nouveau mot de passe (optionnel)' : 'Mot de passe temporaire'}
          rules={[
            {
              required: !isEditMode,
              message: 'Mot de passe requis',
            },
            { min: 6, message: 'Minimum 6 caractères' },
          ]}
        >
          <Input.Password placeholder="Mot de passe sécurisé" />
        </Form.Item>

        <Form.Item
          name="isActive"
          label="Compte actif"
          valuePropName="checked"
        >
          <Switch checkedChildren="Actif" unCheckedChildren="Inactif" />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default UserFormDrawer;

